import { useMutation } from '@tanstack/react-query';
import { moduleOutputAPI } from 'api/module';
import Button from 'components/utils/Button';
import { ModuleContext } from 'components/utils/module/ModuleContext';
import { H1, H5 } from 'components/utils/typo';
import { Variable } from 'hooks/module/useVariables';
import { useContext, useEffect, useRef, useState } from 'react';
import { ApiOutputItem } from './apiOutputItem';

export interface ModuleApiOutputType {
  id: string;
  type: 'variable' | 'string' | 'number' | 'boolean';
  value: string | number | boolean | Variable;
  key: string;
}

export function ApiOutputContainer() {
  const { module } = useContext(ModuleContext);
  const [apiOutputItems, setApiOutputItems] = useState<ModuleApiOutputType[]>(
    module?.api_output || []
  );
  const addNewApiOutputItem = () => {
    setApiOutputItems((prev) => [
      ...prev,
      {
        id: crypto.randomUUID(),
        type: 'variable',
        value: '',
        key: '',
      },
    ]);
  };

  const onChange = (updatedItem: ModuleApiOutputType) => {
    setApiOutputItems((prev) =>
      prev.map((item) => (item.id === updatedItem.id ? updatedItem : item))
    );
  };
  const onDelete = (id: string) => {
    setApiOutputItems((prev) => prev.filter((item) => item.id !== id));
  };

  const { mutateAsync } = useMutation({
    mutationFn: (data: { output: ModuleApiOutputType[] }) =>
      moduleOutputAPI.patch(module!.id || 0, data),
  });

  const queue = useRef<Promise<any>>(Promise.resolve());
  const isFirstRender = useRef(true);

  useEffect(() => {
    if (!module) return;
    if (isFirstRender.current) {
      isFirstRender.current = false;
      setApiOutputItems(module.api_output || []);
      return;
    }
    const timeout = setTimeout(() => {
      queue.current = queue.current.then(() => {
        mutateAsync({ output: apiOutputItems });
      });
    }, 500);
    return () => clearTimeout(timeout);
  }, [apiOutputItems, module]);

  return (
    <div className='p-20'>
      <H1>Module API Output</H1>
      {!module ? null : (
        <div className='lg:max-w-3xl'>
          <div className='mt-10 flex gap-2'>
            <H5 className='flex-1 px-2'>Key</H5>
            <H5 className='flex-1 px-2'>Type</H5>
            <H5 className='flex-1 px-2'>Value</H5>
            <div className='basis-1/12' />
          </div>
          <div className='mt-3 space-y-2'>
            {apiOutputItems.map((item) => (
              <ApiOutputItem key={item.id} item={item} onChange={onChange} onDelete={onDelete} />
            ))}
          </div>
          <div className='mt-10 flex'>
            <div className='flex'>
              <Button.Outline className='px-4' onClick={addNewApiOutputItem}>
                + New
              </Button.Outline>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
